import type {
  ICategoryWithProducts,
  Section,
  MenuItem,
} from '../types';

export const resolveTitle = (
  entity: any,
  locale: string,
  fallback?: string
): string => {
  if (!entity) return fallback || '';
  if (typeof entity === 'string') return entity;
  if (typeof entity === 'object') {
    return (
      entity?.[locale as keyof typeof entity] ||
      entity?.ru ||
      entity?.en ||
      entity?.uz ||
      fallback ||
      ''
    );
  }
  return fallback || '';
};

export const buildSections = (
  categories: ICategoryWithProducts[],
  locale: string
): Section[] => {
  if (!categories.length) return [];

  const normalized: Section[] = [];

  categories.forEach((category) => {
    const parentProducts = category.products || [];
    if (parentProducts.length) {
      normalized.push({
        id: category.id,
        slug: category.slug,
        title: resolveTitle(category.title, locale),
        products: parentProducts,
      });
    }

    category.child_categories?.forEach((child) => {
      const childProducts = child.products || [];
      if (childProducts.length) {
        normalized.push({
          id: child.id,
          slug: child.slug,
          title: resolveTitle(child.title, locale, child.name),
          products: childProducts,
        });
      }
    });
  });

  return normalized;
};

export const buildMenuItems = (
  categories: ICategoryWithProducts[],
  sections: Section[],
  locale: string
): MenuItem[] => {
  const sectionSet = new Set(sections.map((s) => s.id));

  return categories
    .map((category) => {
      const children =
        category.child_categories
          ?.filter((child) => sectionSet.has(child.id))
          ?.map((child) => ({
            id: child.id,
            slug: child.slug,
            title: resolveTitle(child.title, locale, child.name),
          })) || [];

      const hasSection = sectionSet.has(category.id);

      if (!hasSection && children.length === 0) return null;

      return {
        id: category.id,
        slug: category.slug,
        title: resolveTitle(category.title, locale),
        hasSection,
        children,
        image: category.image,
      };
    })
    .filter(Boolean) as MenuItem[];
};
